import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import countries from "i18n-iso-countries";
import { useSearchParams } from "react-router-dom";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import {
  Proposal,
  StewardshipFilterParam,
  VotingAlignmentCompany,
  VotingAlignment as VotingAlignmentType,
  VotingColumnType,
  VotingRecordItem,
} from "../models/stewardshipModels";
import { Filter } from "../types/PageFilterTypes";
import { PaginationParams, SortType } from "../types/ScreenerTypes";
import { DEFAULT_PAGINATION } from "./useStewardshipScreener";
import {
  VotingCompanySummaryDataSource,
  VotingDataSourceType,
} from "../models/tableModels";
import { VOTING_BASE_COLUMNS } from "../pages/CompanyProfile/constants";
import {
  buildVotingDataSource,
  getDynamicColumns,
} from "../pages/CompanyProfile/tableUtils";
import { buildVotingCompanySummaryDataSource } from "../pages/StewardshipHomepage/utils";
import {
  buildVotingRecords,
  calculateVotingAlignment,
} from "../components/common/Voting/utils";
import { MESSAGES } from "../messages";
import { generateFilters } from "../utils/tableUtils";
import { VOTING_SCREENER_BASE_COLUMNS } from "../pages/StewardshipHomepage/constants";
import { ColumnType } from "antd/es/table";

export type TableChangeParams = {
  pagination: PaginationParams;
  filters: Record<string, FilterValue | null>;
  sorter: SorterResult<VotingCompanySummaryDataSource>;
};

type VotingCompanySummaryFilters = {
  countryIso2?: string[];
  categories?: string[];
};

const VOTING_ALIGNMENT_PLACEHOLDER_OPTION: Filter = {
  key: null,
  displayValue: "Select alignment reference",
};

const DEFAULT_VOTING_FILTERS: VotingCompanySummaryFilters = {};

export const transformSorter = (
  sorter: SorterResult<VotingCompanySummaryDataSource>,
): SortType | false => {
  if (!sorter.field || !sorter.order) return false;

  return {
    key: String(sorter.field),
    sort: sorter.order === "ascend" ? "asc" : "desc",
    is_metric_sort: false,
  };
};

export const useVotingAlignment = (
  filters: StewardshipFilterParam,
  isVotingRecords = false,
): UseVotingAlignment => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Records States
  const [votingProposalsData, setVotingProposalsData] = useState<Proposal[]>(
    [],
  );

  // Summary States
  const [votingSummaryPagination, setVotingSummaryPagination] =
    useState<PaginationParams>(DEFAULT_PAGINATION);
  const [activeFilters, setActiveFilters] =
    useState<VotingCompanySummaryFilters>(DEFAULT_VOTING_FILTERS);
  const [sorter, setSorter] = useState<
    SorterResult<VotingCompanySummaryDataSource>
  >({});

  // Shared states
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const [votingAlignmentData, setVotingAlignmentData] = useState<
    VotingAlignmentType[]
  >([]);
  const [votingAlignmentReference, setVotingAlignmentReference] =
    useState<Filter>(VOTING_ALIGNMENT_PLACEHOLDER_OPTION);

  const [shouldDisplayVotingRecordModal, setShouldDisplayVotingRecordModal] =
    useState<boolean>(false);
  const [modalVotingRecordId, setModalVotingRecordId] = useState<string | null>(
    null,
  );
  const [dataSource, setDataSource] = useState<VotingDataSourceType[]>([]);
  const { addToast } = useToast();
  const scrollTargetRef = useRef<HTMLDivElement>(null);

  // Records API Calls
  const {
    data,
    sendData: getVotingData,
    isLoading: isVotingDataLoading,
    error: getVotingDataError,
  } = usePost<VotingRecordItem[], StewardshipFilterParam>();

  // Summary API Calls
  const {
    data: votingSummaryData,
    sendData: getVotingSummaryData,
    isLoading: isVotingSummaryDataLoading,
    error: getVotingSummaryDataError,
  } = usePost<VotingAlignmentType[], StewardshipFilterParam>();

  const {
    data: votingCompanySummaryData,
    sendData: getVotingCompanySummaryData,
    isLoading: isCompanyVotingSummaryLoading,
    error: getVotingCompanySummaryError,
  } = usePost<VotingAlignmentCompany[], StewardshipFilterParam>();

  const {
    data: votingCompanySummaryConfigData,
    sendData: getVotingCompanySummaryConfigData,
    isLoading: isCompanyVotingSummaryConfigLoading,
    error: getVotingCompanySummaryConfigError,
  } = usePost<
    { categories: string[]; countries: string[] },
    StewardshipFilterParam
  >();

  // Summary Memoization
  const votingCompanySummaryRecords: VotingCompanySummaryDataSource[] =
    useMemo(() => {
      return !isVotingRecords
        ? buildVotingCompanySummaryDataSource(votingCompanySummaryData || [])
        : [];
    }, [votingCompanySummaryData, isVotingRecords]);

  const votingCompanySummaryColumns = useMemo(() => {
    const iso2CountryCodes = votingCompanySummaryConfigData?.countries ?? [];
    const countryFilters = generateFilters(iso2CountryCodes).map((filter) => ({
      ...filter,
      text: countries.getName(filter.value, "en") ?? "Unknown",
    }));

    const categoriesList = votingCompanySummaryConfigData?.categories ?? [];
    const categoryFilters = generateFilters(categoriesList);

    return VOTING_SCREENER_BASE_COLUMNS.map((col) => {
      if (col.key === "countryIso2") {
        return {
          ...col,
          filters: countryFilters,
          filteredValue: activeFilters.countryIso2 || [],
        };
      }

      if (col.key === "categories") {
        return {
          ...col,
          filters: categoryFilters,
          filteredValue: activeFilters.categories || [],
        };
      }

      return col;
    }) as ColumnType<VotingCompanySummaryDataSource>[];
  }, [votingCompanySummaryConfigData, activeFilters]);

  // Shared Memoization
  const shouldDisplayVotingAlignment: boolean = votingAlignmentData.length > 0;
  const isLoading: boolean =
    isVotingDataLoading ||
    isVotingSummaryDataLoading ||
    isCompanyVotingSummaryLoading;

  const votingTableColumns: VotingColumnType[] = useMemo(
    () => [...VOTING_BASE_COLUMNS, ...getDynamicColumns(votingProposalsData)],
    [votingProposalsData],
  );

  // Records Functions
  const handleShowVotingRecordModal = useCallback(
    (recordId: string): void => {
      setShouldDisplayVotingRecordModal(true);
      setModalVotingRecordId(recordId);
      setSearchParams(
        (prev) => {
          prev.set("votingRecordId", recordId);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleHideVotingRecordModal = useCallback((): void => {
    setShouldDisplayVotingRecordModal(false);
    setModalVotingRecordId(null);
    setSearchParams(
      (prev) => {
        prev.delete("votingRecordId");
        return prev;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Shared Functions
  const buildManagerOptions = (data: VotingAlignmentType[]): Filter[] => {
    return data.map((item) => ({
      key: item.manager_id || item.managerId,
      displayValue: item.manager,
    }));
  };

  const handleTableChange = ({
    pagination,
    filters,
    sorter,
  }: TableChangeParams): void => {
    // Handle Filters we only send the filters that are not empty to the API
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(
        (
          [_, value], //eslint-disable-line
        ) => (Array.isArray(value) ? value.length > 0 : !!value),
      ),
    );
    setActiveFilters(cleanedFilters as VotingCompanySummaryFilters);

    // Handle Pagination
    setVotingSummaryPagination(pagination);

    // Handle Sorting
    setSorter(sorter);
  };

  const handleCardExpand = (isActive: boolean): void => {
    setIsCardExpanded(isActive);
  };

  // Summary UseEffects
  useEffect(() => {
    if (!isVotingRecords) {
      getVotingSummaryData("GET_VOTING_SUMMARY", {
        ...filters,
        voting_manager_reference: votingAlignmentReference.key as string,
      });

      const sorterParams: SortType | false = transformSorter(sorter);

      // Fetch config data
      getVotingCompanySummaryConfigData("GET_COMPANIES_VOTING_SUMMARY", {
        ...filters,
        voting_manager_reference: votingAlignmentReference.key as string,
        is_config: true,
      });

      // Fetch paginated data
      getVotingCompanySummaryData("GET_COMPANIES_VOTING_SUMMARY", {
        ...filters,
        pagination: votingSummaryPagination,
        ...activeFilters,
        voting_manager_reference: votingAlignmentReference.key as string,
        ...(sorterParams && { sort: sorterParams }),
      });
    }
  }, [
    filters,
    votingAlignmentReference,
    votingSummaryPagination,
    activeFilters,
    isVotingRecords,
  ]);

  // Records UseEffects
  useEffect(() => {
    if (isVotingRecords) {
      getVotingData("GET_VOTING_DATA", filters);
    }
  }, [filters, isVotingRecords]);

  // Shared UseEffects
  useEffect(() => {
    if (data) {
      const proposalData = buildVotingRecords(data);
      setVotingProposalsData(proposalData);
      setVotingAlignmentData(calculateVotingAlignment(proposalData));

      // Auto-open modal if votingRecordId is in URL
      const urlVotingRecordId = searchParams.get("votingRecordId");
      if (urlVotingRecordId) {
        const recordExists = data.some(
          (item) => item.voting_record_id === urlVotingRecordId,
        );
        if (recordExists) {
          setIsCardExpanded(true);
          setShouldDisplayVotingRecordModal(true);
          setModalVotingRecordId(urlVotingRecordId);
          // Scroll to voting section after a brief delay for render
          setTimeout(() => {
            scrollTargetRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 300);
        }
      }
    }
    if (votingSummaryData) {
      setVotingAlignmentData(votingSummaryData);
    }
  }, [data, votingSummaryData]);

  useEffect(() => {
    if (
      getVotingDataError ||
      getVotingSummaryDataError ||
      getVotingCompanySummaryError ||
      getVotingCompanySummaryConfigError
    ) {
      addToast(
        MESSAGES.SCREENING_TOOL.ERROR_FETCHING_VOTING_ALIGNMENT,
        "error",
      );
    }
  }, [
    getVotingDataError,
    getVotingSummaryDataError,
    getVotingCompanySummaryError,
    addToast,
  ]);

  useEffect(() => {
    setVotingAlignmentData(
      calculateVotingAlignment(
        votingProposalsData,
        votingAlignmentReference?.key,
      ),
    );
  }, [votingAlignmentReference, votingProposalsData]);

  useEffect(() => {
    setDataSource(
      buildVotingDataSource(votingProposalsData, votingAlignmentReference.key),
    );
  }, [votingProposalsData, votingAlignmentReference.key]);

  const managerOptions = useMemo(
    () => buildManagerOptions(votingAlignmentData),
    [votingAlignmentData],
  );

  return {
    // Data
    votingAlignmentData,
    votingProposalsData,
    dataSource,
    votingCompanySummaryRecords,
    rawVotingData: data || [],

    // Config Data
    categories: votingCompanySummaryConfigData?.categories || [],
    countries: votingCompanySummaryConfigData?.countries || [],

    // State
    isCardExpanded,
    shouldDisplayVotingAlignment,
    shouldDisplayVotingRecordModal,
    modalVotingRecordId,
    votingAlignmentReference,
    votingSummaryPagination,

    // Loading States
    isLoading,
    isCompanyVotingSummaryLoading,
    isCompanyVotingSummaryConfigLoading,

    // Table Configuration
    votingTableColumns,
    totalRecords: votingCompanySummaryRecords.length
      ? votingCompanySummaryRecords[0].totalRecordsCount
      : 0,

    // Options
    managerOptions,
    placeholderOption: VOTING_ALIGNMENT_PLACEHOLDER_OPTION,

    // Sorter
    sorter,

    // Actions
    handleShowVotingRecordModal,
    handleHideVotingRecordModal,
    handleCardExpand,
    setVotingAlignmentReference,
    setVotingSummaryPagination,
    handleTableChange,

    votingCompanySummaryColumns,

    // Refs
    scrollTargetRef,
  };
};

export interface UseVotingAlignment {
  // Data
  votingAlignmentData: VotingAlignmentType[];
  votingProposalsData: Proposal[];
  dataSource: VotingDataSourceType[];
  votingCompanySummaryRecords: VotingCompanySummaryDataSource[];
  rawVotingData: VotingRecordItem[];

  // Config Data
  categories: string[];
  countries: string[];

  // State
  isCardExpanded: boolean;
  shouldDisplayVotingAlignment: boolean;
  shouldDisplayVotingRecordModal: boolean;
  modalVotingRecordId: string | null;
  votingAlignmentReference: Filter;
  votingSummaryPagination: PaginationParams;

  // Loading States
  isLoading: boolean;
  isCompanyVotingSummaryLoading: boolean;
  isCompanyVotingSummaryConfigLoading: boolean;

  // Table Configuration
  votingTableColumns: VotingColumnType[];
  totalRecords: number;

  // Options
  managerOptions: Filter[];
  placeholderOption: Filter;

  //   columns
  votingCompanySummaryColumns: ColumnType<VotingCompanySummaryDataSource>[];

  // Sorter
  sorter: SorterResult<VotingCompanySummaryDataSource>;

  // Actions
  handleTableChange: (params: TableChangeParams) => void;
  handleShowVotingRecordModal: (recordId: string) => void;
  handleHideVotingRecordModal: () => void;
  handleCardExpand: (isActive: boolean) => void;
  setVotingAlignmentReference: (filter: Filter) => void;
  setVotingSummaryPagination: (pagination: PaginationParams) => void;

  // Refs
  scrollTargetRef: React.RefObject<HTMLDivElement>;
}
